import { useCallback, useState } from 'react';
import { Module, ScopeEnum } from 'src/domain';
import { useChainId } from 'src/hooks/chain';
import { useDiscardChangesModule } from 'src/hooks/discard-changes/useDiscardChangesModule';
import { useFurnitureTypeQuery } from 'src/hooks/inventory-master-data/queries/useFurnitureTypesQuery';
import { useInventoryMasterDataQuery } from 'src/hooks/inventory-master-data/queries/useInventoryMasterDataQuery';
import { useChangesSnackbar } from 'src/hooks/snackbar/useChangesSnackbar';
import { useStorefrontType } from 'src/hooks/style-timeline/useStorefrontType';
import { useStoreType } from 'src/hooks/useStoreType';
import { CreateStoreFurnitureInput } from 'src/infrastructure/rest-api/api-types';
import { useApiMutation } from 'src/infrastructure/rest-api/useApi';
import { isFurnitureColumnType } from 'src/utils/furnitureUtils';
import { toFurnitureColumnKey } from '../table/useStoreInventoryRows';
import { useStoreInventorySimulationRows } from '../table/useStoreInventorySimulationRows';
import { mapFurnitureZoneChangesToPayload } from './storeInventoryZonePayload';
import { useStoreInventoryUnsavedChanges } from './useStoreInventoryUnsavedChanges';

export type StoreInventoryEditStep = 'options' | 'zones';

export const useStoreInventorySaveChanges = (editStep: StoreInventoryEditStep = 'options') => {
    const chainId = useChainId();
    const storeType = useStoreType();
    const storefrontType = useStorefrontType();
    const { data: simulationRows } = useStoreInventorySimulationRows();
    const discardChanges = useDiscardChangesModule(
        Module.StoreInventoryManagement,
        ScopeEnum.GLOBAL,
        chainId ?? undefined
    );
    const [isUpdating, setIsUpdating] = useState(false);
    const showSnackbar = useChangesSnackbar();
    const { data: inventoryMasterData } = useInventoryMasterDataQuery();
    const { data: furnitureTypes } = useFurnitureTypeQuery();
    const [unsavedChanges] = useStoreInventoryUnsavedChanges();
    const [updateMutation] = useApiMutation('/api/inventorymanagement/stores', 'post', {
        update(_data, _variables, client) {
            client.invalidateQueries({
                queryKey: [
                    `/api/inventorymanagement/chains/${chainId}/stores`,
                    {
                        storefrontType,
                        storeType,
                    },
                ],
            });
        },
    });

    const updateStores = useCallback(
        (payload: CreateStoreFurnitureInput[]): Promise<CreateStoreFurnitureInput[]> => {
            return updateMutation({
                body: Array.from(payload),
            });
        },
        [updateMutation]
    );

    const sendUpdateQuery = useCallback(async () => {
        if (editStep === 'zones') {
            const payload = mapFurnitureZoneChangesToPayload(unsavedChanges);

            setIsUpdating(true);
            return updateStores(payload);
        }

        const payloads: Map<number, CreateStoreFurnitureInput[]> = new Map();

        const changedRows = simulationRows.filter((row) => unsavedChanges.has(row.id));
        for (const row of changedRows) {
            for (const item of inventoryMasterData || []) {
                const furniture = furnitureTypes?.find((type) => type.id === item.furnitureTypeId);
                const data = row.furnitureColumns?.[toFurnitureColumnKey(furniture, item)];
                if (!isFurnitureColumnType(data)) continue;
                const numberOfOptions = data.optionCount;
                if (!payloads.has(row.id)) {
                    payloads.set(row.id, []);
                }
                payloads.get(row.id)!.push({
                    storeId: row.id,
                    chainFurnitureId: data.chainFurnitureId,
                    numberOfOptions,
                    chainFurnitureZoneId: data.chainFurnitureZoneId,
                });
            }
        }

        setIsUpdating(true);
        const payload = Array.from(payloads.values()).flat();
        return updateStores(payload);
    }, [editStep, updateStores, simulationRows, inventoryMasterData, furnitureTypes, unsavedChanges]);

    const saveChanges = useCallback(async () => {
        setIsUpdating(true);
        try {
            const response = await sendUpdateQuery();
            showSnackbar();
            discardChanges();
            return response;
        } catch (err) {
            showSnackbar(err);
        } finally {
            setIsUpdating(false);
        }
    }, [sendUpdateQuery, discardChanges, showSnackbar]);

    return [saveChanges, { loading: isUpdating }] as const;
};
